import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createSale, syncPayment, syncCashless, getSaleDetail,
  resumePayment,
  requestVoid,
  requestRefund,
  withdrawApprovalRequest,
} from '@/api/sale.api';
import { validateCoupon, applyCoupon } from '@/api/coupon.api';
import { printBatch, printSingle, reprintBatch, reprintSingle } from '@/api/qrCode.api';
import type { ReprintInput } from '@/api/qrCode.api';
import { bindUserDevice, type BindDevicePayload } from '@/api/device.api';
import { useDeviceStore } from '@/stores/device.store';
import type { CreateSalePayload, RefundRequestInput } from '@/types/sale';
import type { CouponCartItem } from '@/types/coupon';

export function useCreateSale() {
  return useMutation({
    mutationKey: ['sale', 'create'],
    mutationFn: (args: { payload: CreateSalePayload; idempotencyKey?: string }) =>
      createSale(args.payload, args.idempotencyKey),
  });
}

export function useValidateCoupon() {
  return useMutation({
    mutationKey: ['coupon', 'validate'],
    mutationFn: (code: string) => validateCoupon(code),
  });
}

/**
 * Apply a coupon to the current cart. Unlike `useValidateCoupon` this returns
 * the real `discountAmount`, which is what the cart total and the "Hemat …"
 * line are driven from.
 */
export function useApplyCoupon() {
  return useMutation({
    mutationKey: ['coupon', 'apply'],
    mutationFn: (args: {
      code: string;
      cart: { items: CouponCartItem[]; totalAmount: number };
      visitDate?: string;
    }) => applyCoupon(args.code, args.cart, args.visitDate),
  });
}

export function usePrintBatch() {
  return useMutation({
    mutationKey: ['print', 'batch'],
    // A plain invoice id, or `{ invoiceId, headcount }` for a partner group claim (ABT-S22).
    mutationFn: (arg: string | { invoiceId: string; headcount?: number }) =>
      typeof arg === 'string' ? printBatch(arg) : printBatch(arg.invoiceId, { headcount: arg.headcount }),
  });
}

/**
 * FIRST print of one wristband. The server refuses a second print
 * (409 ALREADY_PRINTED) — a further copy goes through `useReprintWithNewQr`.
 */
export function usePrintSingle() {
  return useMutation({
    mutationKey: ['print', 'single'],
    mutationFn: (qrId: string) => printSingle(qrId),
  });
}

/** Reprint one already-printed wristband — mints a NEW QR, reason required. */
export function useReprintWithNewQr() {
  return useMutation({
    mutationKey: ['reprint', 'single'],
    mutationFn: (args: { qrId: string } & ReprintInput) =>
      reprintSingle(args.qrId, { reason: args.reason, note: args.note }),
  });
}

/** Reprint every already-printed wristband on an invoice — new QRs, reason required. */
export function useReprintBatch() {
  return useMutation({
    mutationKey: ['reprint', 'batch'],
    mutationFn: (args: { invoiceId: string } & ReprintInput) =>
      reprintBatch(args.invoiceId, { reason: args.reason, note: args.note }),
  });
}

export function useSaleDetail() {
  return useMutation({
    mutationKey: ['sale', 'detail'],
    mutationFn: (invoiceId: string) => getSaleDetail(invoiceId),
  });
}

/**
 * Re-open an unpaid sale. The server syncs with the gateway first, so this can
 * come back already SUCCESS for a payment whose webhook never arrived.
 */
export function useResumePayment() {
  return useMutation({
    mutationKey: ['sale', 'resume'],
    mutationFn: (invoiceId: string) => resumePayment(invoiceId),
  });
}

export function useSyncPayment() {
  return useMutation({
    mutationKey: ['sync', 'payment'],
    mutationFn: (invoiceId: string) => syncPayment(invoiceId),
  });
}

/** @deprecated use {@link useSyncPayment} — every gateway, not only Midtrans. */
export const useSyncMidtrans = useSyncPayment;

export function useSyncCashless() {
  return useMutation({
    mutationKey: ['sync', 'cashless'],
    mutationFn: (invoiceId: string) => syncCashless(invoiceId),
  });
}

export function useBindDevice() {
  const qc = useQueryClient();
  const hydrate = useDeviceStore((s) => s.hydrateFromBinding);
  return useMutation({
    mutationKey: ['device', 'bind'],
    mutationFn: (payload: BindDevicePayload) => bindUserDevice(payload),
    onSuccess: (binding) => {
      hydrate(binding);
      qc.invalidateQueries({ queryKey: ['deviceBinding'] });
    },
  });
}

/** Refresh everything a void/refund request touches. */
function invalidateApprovals(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['approvalRequests'] });
  qc.invalidateQueries({ queryKey: ['shiftSummary'] });
}

/** Ask a supervisor to void a paid sale (nothing changes until approved). */
export function useRequestVoid() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['approval', 'void'],
    mutationFn: (args: { saleRef: string; reason: string; idempotencyKey?: string }) =>
      requestVoid(args.saleRef, args.reason, args.idempotencyKey),
    onSuccess: () => invalidateApprovals(qc),
  });
}

/** Ask a supervisor to refund a paid sale, in full or by line. */
export function useRequestRefund() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['approval', 'refund'],
    mutationFn: (args: { saleRef: string; input: RefundRequestInput; idempotencyKey?: string }) =>
      requestRefund(args.saleRef, args.input, args.idempotencyKey),
    onSuccess: () => invalidateApprovals(qc),
  });
}

/** Withdraw one of the operator's own PENDING requests. */
export function useWithdrawApprovalRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: ['approval', 'withdraw'],
    mutationFn: (id: string) => withdrawApprovalRequest(id),
    onSuccess: () => invalidateApprovals(qc),
  });
}
